import { AgGridAngular } from "@ag-grid-community/angular";
import { GridOptions, ICellRendererParams } from "@ag-grid-community/core";
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input } from "@angular/core";
import { takeUntilDestroyed, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { gridConfig } from "@front/m19-grid-config";
import {
  ACOS,
  AD_SALES,
  CLICK_THROUGH_RATE,
  CLICKS,
  CONVERSION_RATE,
  COST,
  CPC,
  IMPRESSIONS,
  Metric,
  ROAS,
} from "@front/m19-metrics";
import { AdStatsEx, StrategyEx } from "@front/m19-models";
import { DataSetEventAnnotation } from "@front/m19-models/dataset";
import {
  AccountSelectionService,
  AdStatsType,
  AuthService,
  CurrencyService,
  MetricsSelectorPage,
  StatsService,
  UserSelectionService,
} from "@front/m19-services";
import { ActionButton, ActionButtonsComponent, ModalOptions, ModalService, SpinnerComponent } from "@front/m19-ui";
import { getOppositeAdStats, mergeSeveralDates, sumAdStats } from "@front/m19-utils";
import { TranslocoService } from "@jsverse/transloco";
import { actionColumnProperties } from "@m19-board/grid-config/grid-columns";
import { GridConfigService } from "@m19-board/grid-config/grid-config.service";
import {
  ChartData,
  ChartModalComponent,
  ChartModalInputData,
} from "@m19-board/shared/chart-modal/chart-modal.component";
import { combineLatest, combineLatestWith, map, Observable, switchMap } from "rxjs";
import { ActivityEventAnnotationsService } from "../../../activities/activity-event-annotations.service";

interface AudienceStatsGridData extends AdStatsEx {
  path: string[];
  name: string;
}

enum AudienceStatsRowType {
  STRATEGY = "strategy",
  IN_AUDIENCE = "in_audience",
  NOT_IN_AUDIENCE = "not_in_audience",
}

@Component({
  selector: "app-audience-stats",
  imports: [AgGridAngular, SpinnerComponent],
  templateUrl: "./audience-stats.component.html",
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AudienceStatsComponent {
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly gridConfigService = inject(GridConfigService);
  private readonly statsService = inject(StatsService);
  private readonly translocoService = inject(TranslocoService);
  private readonly modalService = inject(ModalService);
  private readonly activityEventAnnotationsService = inject(ActivityEventAnnotationsService);
  private readonly currencyService = inject(CurrencyService);

  readonly METRIC_COLUMNS: Metric<AdStatsEx>[] = [
    AD_SALES,
    COST,
    ACOS,
    CLICKS,
    IMPRESSIONS,
    CLICK_THROUGH_RATE,
    CONVERSION_RATE,
    CPC,
    ROAS,
  ];
  readonly PARENT_ROW_NAME = this.translocoService.translate("strategy-page.strategy_stats");
  readonly NOT_IN_AUDIENCE_ROW_NAME = this.translocoService.translate("strategy-page.not_in_audience");
  readonly IN_AUDIENCE_ROW_NAME = this.translocoService.translate("strategy-page.in_audience");

  strategy = input.required<StrategyEx>();
  strategy$ = toObservable(this.strategy);

  private currency = toSignal(this.userSelectionService.selectedCurrency$);
  private locale = toSignal(this.authService.loggedUser$.pipe(map((user) => user.locale)));

  private readonly dailyStrategyStats$ = combineLatest([
    this.accountSelectionService.singleAccountMarketplaceSelection$,
    this.userSelectionService.dateRange$,
    this.userSelectionService.selectedCurrency$,
  ]).pipe(
    takeUntilDestroyed(this.destroyRef),
    switchMap(([am, dr, currency]) =>
      this.statsService.getDailyCampaignStats(am.accountId, am.marketplace, currency, dr[0], dr[1]).pipe(
        combineLatestWith(this.strategy$),
        map(([data, strategy]) => data.filter((d) => d.strategyId === strategy.strategyId)),
      ),
    ),
  );
  private readonly totalStrategyStats$ = this.dailyStrategyStats$.pipe(
    map((data) => data.reduce((a, b) => mergeSeveralDates(a, b), {})),
  );
  private readonly totalStrategyStats = toSignal<AdStatsEx>(this.totalStrategyStats$);

  private readonly dailyAudienceBoostStats$ = combineLatest([
    this.userSelectionService.dateRange$,
    this.accountSelectionService.singleAccountMarketplaceSelection$,
    this.userSelectionService.selectedCurrency$,
  ]).pipe(
    takeUntilDestroyed(this.destroyRef),
    switchMap(([dateRange, accountMarketplace, currency]) =>
      this.statsService
        .getDailyAudienceBoostStats(
          accountMarketplace.accountId,
          accountMarketplace.marketplace,
          currency,
          dateRange[0],
          dateRange[1],
        )
        .pipe(
          combineLatestWith(this.strategy$),
          map(([data, strategy]) => data.filter((d) => d.strategyId === strategy.strategyId)),
        ),
    ),
  );
  private readonly totalAudienceBoostStats$ = this.dailyAudienceBoostStats$.pipe(
    map((data) => data.reduce((a, b) => mergeSeveralDates(a, b), {})),
  );
  private readonly totalAudienceBoostStats = toSignal<AdStatsEx>(this.totalAudienceBoostStats$);

  private readonly dailyNotInAudienceStats$ = combineLatest([
    this.dailyStrategyStats$,
    this.dailyAudienceBoostStats$,
  ]).pipe(
    map(([strategyStats, audienceBoostStats]) => {
      const oppositeDailyAudienceBoostStats = audienceBoostStats!.map((d) => getOppositeAdStats(d));
      return [...strategyStats!, ...oppositeDailyAudienceBoostStats];
    }),
  );
  private readonly totalNotInAudienceStats$ = this.dailyNotInAudienceStats$.pipe(
    map((data) => data.reduce((a, b) => mergeSeveralDates(a, b), {})),
  );
  private readonly totalNotInAudienceStats = toSignal<AdStatsEx>(this.totalNotInAudienceStats$);

  gridData = computed<AudienceStatsGridData[] | undefined>(() => {
    if (!this.totalStrategyStats() || !this.totalAudienceBoostStats() || !this.totalNotInAudienceStats())
      return undefined;
    return this.computeGridData(
      this.totalAudienceBoostStats()!,
      this.totalStrategyStats()!,
      this.totalNotInAudienceStats()!,
    );
  });

  gridOptions: GridOptions<AudienceStatsGridData> = {
    ...gridConfig({
      gridKey: "audienceStats",
    }),
    groupDefaultExpanded: 1,
    columnDefs: [
      ...this.gridConfigService
        .getMetricsColDef<AudienceStatsGridData>(this.METRIC_COLUMNS, false)
        .map(({ headerName, ...def }) => ({
          ...def,
          cellRendererParams: (params: any) => {
            return {
              ...(def as any).cellRendererParams(params),
              previousData: undefined,
              currency: this.currency(),
              locale: this.locale(),
            };
          },
        })),
      {
        ...actionColumnProperties<AudienceStatsGridData, string>(),
        cellRendererSelector: (params: ICellRendererParams<AudienceStatsGridData>) => {
          if (params.node.isRowPinned()) return undefined;
          const actionBtns: ActionButton[] = [
            {
              icon: "chartLine",
              onClick: () => {
                if (!params.data) return;
                const rowType = params.data.path[params.data.path.length - 1] as AudienceStatsRowType;
                this.displayRowChart(rowType);
              },
            },
          ];

          return { component: ActionButtonsComponent, params: { actionButtons: actionBtns } };
        },
      },
    ],
    autoGroupColumnDef: {
      headerName: "",
      field: "name",
      cellRendererParams: {
        suppressCount: true,
      },
    },
    getDataPath: (data) => data.path,
  };

  private displayRowChart(rowType: AudienceStatsRowType) {
    const chartData = this.getChartData(rowType);
    let annotations$: Observable<DataSetEventAnnotation[]> | undefined = undefined;
    annotations$ = this.activityEventAnnotationsService.getStrategiesActivityEventAnnotation([this.strategy()]);

    const splitData =
      rowType === AudienceStatsRowType.STRATEGY
        ? {
            splitData1: this.totalAudienceBoostStats()!,
            splitData2: this.totalNotInAudienceStats()!,
            type: AdStatsType.AUDIENCE,
          }
        : undefined;

    const modalOption: ModalOptions<ChartModalInputData<AdStatsEx>> = {
      modalTitle: this.getChartTitle(rowType),
      data: {
        locale: this.locale()!,
        currency: this.currency()!,
        dataSetConfiguration: {
          aggregationFunction: [sumAdStats],
        },
        chartData$: chartData,
        selectedMetrics: [AD_SALES, COST],
        metrics: this.METRIC_COLUMNS,
        localStorageKey: { page: MetricsSelectorPage.audienceStats },
        annotations$,
        splitData,
      },
    };
    this.modalService.openModal<ChartModalInputData<AdStatsEx>, void>(ChartModalComponent, modalOption);
  }

  private getChartData(rowType: AudienceStatsRowType): Observable<ChartData<AdStatsEx>> {
    switch (rowType) {
      case AudienceStatsRowType.STRATEGY:
        return combineLatest([
          this.dailyAudienceBoostStats$,
          this.dailyNotInAudienceStats$,
          this.totalStrategyStats$,
        ]).pipe(
          map(([audienceBoostStats, notInAudienceStats, totalStrategyStats]) => {
            const convertedAudienceStats = audienceBoostStats!.map((d) => ({
              ...d,
              statsType: AdStatsType.AUDIENCE,
            }));
            const convertedNotInAudienceStats = notInAudienceStats!.map((d) => ({
              ...d,
              statsType: AdStatsType.NOT_IN_AUDIENCE,
            }));
            return {
              data: [...convertedAudienceStats, ...convertedNotInAudienceStats],
              totalData: totalStrategyStats!,
            };
          }),
        );
      case AudienceStatsRowType.IN_AUDIENCE:
        return combineLatest([this.dailyAudienceBoostStats$, this.totalAudienceBoostStats$]).pipe(
          map(([data, totalData]) => ({ data: data!, totalData: totalData! })),
        );
      case AudienceStatsRowType.NOT_IN_AUDIENCE:
        return combineLatest([this.dailyNotInAudienceStats$, this.totalNotInAudienceStats$]).pipe(
          map(([data, totalData]) => ({ data: data!, totalData: totalData! })),
        );
    }
  }

  private getChartTitle(rowType: AudienceStatsRowType): string {
    switch (rowType) {
      case AudienceStatsRowType.STRATEGY:
        return this.translocoService.translate("strategy-page.strategy_stats");
      case AudienceStatsRowType.IN_AUDIENCE:
        return this.translocoService.translate("strategy-page.audience_stats");
      case AudienceStatsRowType.NOT_IN_AUDIENCE:
        return this.translocoService.translate("strategy-page.not_in_audience_stats");
    }
  }

  private computeGridData(
    totalAudienceBoostStats: AdStatsEx,
    totalStrategyStats: AdStatsEx,
    totalNotInAudienceStats: AdStatsEx,
  ): AudienceStatsGridData[] {
    return [
      {
        ...totalStrategyStats,
        path: [AudienceStatsRowType.STRATEGY],
        name: this.PARENT_ROW_NAME,
      },
      {
        ...totalAudienceBoostStats,
        path: [AudienceStatsRowType.STRATEGY, AudienceStatsRowType.IN_AUDIENCE],
        name: this.IN_AUDIENCE_ROW_NAME,
      },
      {
        ...totalNotInAudienceStats,
        path: [AudienceStatsRowType.STRATEGY, AudienceStatsRowType.NOT_IN_AUDIENCE],
        name: this.NOT_IN_AUDIENCE_ROW_NAME,
      },
    ];
  }
}
